// ProfiloPubblico.tsx — /@nome: il link personale. Chi lo apre vede il nome e può mandare una sfida; la sfida
// arriva nell'app del proprietario, che la accetta quando vuole (finché il link della sfida non scade).
import { AvvisoSospensione } from "../componenti/AvvisoSospensione.tsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { CampoNome } from "../gioco/CampoNome.tsx";
import { ErroreRete } from "../servizi/api.ts";
import { useAccount } from "../servizi/account.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { salvaToken } from "../servizi/sfide.ts";
import { leggiProfiloPubblico, sfidaProfilo, type ProfiloPubblico as Profilo } from "../servizi/social.ts";
import { vai } from "../servizi/percorso.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { PERCORSO_SFIDA, PREFERENZE } from "../costanti.ts";
import { PulsanteAmicizia } from "./PulsanteAmicizia.tsx";
import { bloccaPersona, chiediAmiciziaProfilo, sbloccaPersona } from "../servizi/amici.ts";
import { Finestra } from "../componenti/Finestra.tsx";

const ERRORI_NOTI = ["link_spento", "troppe_richieste", "quota_finita", "troppe_sfide", "sospeso", "gioco_spento", "offline", "timeout", "nome_vietato"];

export function ProfiloPubblico({ nomeUtente, tema, onEsci }: { nomeUtente: string; tema: ControlloTema; onEsci: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [p, setP] = useState<Profilo | null | "assente">(null);
  const [nome, setNome] = useState(() => (account.tipo === "registrato" ? account.io.nome : (leggiPreferenza(PREFERENZE.nome) ?? "")));
  const [errore, setErrore] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);
  const [chiediBlocco, setChiediBlocco] = useState(false); // v039
  const [giro, setGiro] = useState(0);

  useEffect(() => {
    let vivo = true;
    leggiProfiloPubblico(nomeUtente)
      .then((x) => vivo && setP(x))
      .catch(() => vivo && setP("assente"));
    return () => {
      vivo = false;
    };
  }, [nomeUtente, giro]);

  const mio = account.tipo === "registrato" && p !== null && p !== "assente" && account.io.nomeUtente === p.nomeUtente;

  const manda = async () => {
    if (!p || p === "assente" || !nome.trim()) return;
    setErrore(null);
    setInvio(true);
    try {
      salvaPreferenza(PREFERENZE.nome, nome.trim());
      const s = await sfidaProfilo(p.nomeUtente, nome.trim());
      salvaToken(s.codice, s.token, s.per.nome);
      vai(`${PERCORSO_SFIDA}${s.codice}`, true);
    } catch (e) {
      const c = e instanceof ErroreRete ? e.codice : "generico";
      setErrore(ERRORI_NOTI.includes(c) ? c : "generico");
    } finally {
      setInvio(false);
    }
  };

  if (p === null)
    return (
      <div className="pagina schermata-semplice" aria-busy="true">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{t("generale.caricamento")}</p>
      </div>
    );
  if (p === "assente")
    return (
      <div className="pagina schermata-semplice">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{t("social.profilo_assente")}</p>
        <button type="button" className="pulsante pulsante-primario" onClick={onEsci}>
          {t("gioco.torna_home")}
        </button>
      </div>
    );

  return (
    <div className="pagina invito" data-profilo={p.nomeUtente}>
      <main className="invito-centro">
        <Faccina tema={tema.tema} quale={p.avatar ?? "mistero"} />
        <h1 className="invito-titolo">{t("social.profilo_titolo", { nome: p.nome })}</h1>
        <p className="nota-centrata" translate="no">
          @{p.nomeUtente}
        </p>
        {mio ? (
          <p className="invito-testo">{t("social.profilo_tuo")}</p>
        ) : p.bloccato ? (
          <div className="ld-profilo-bloccato">
            <p className="invito-testo">{t("amici.profilo_bloccato", { nome: p.nome })}</p>
            <button type="button" className="pulsante pulsante-secondario" onClick={() => p.id && void sbloccaPersona(p.id).finally(() => setGiro((g) => g + 1))}>
              {t("amici.sblocca")}
            </button>
          </div>
        ) : !p.accetta ? (
          // v043: la stessa frase gentile sul telefono e sul computer, che sia il link spento o un blocco (senza dire quale)
          <p className="invito-testo ld-non-sfidabile">{t("social.profilo_non_sfidabile", { nome: p.nome })}</p>
        ) : (
          <form
            className="invito-modulo"
            onSubmit={(e) => {
              e.preventDefault();
              void manda();
            }}
          >
            <p className="invito-testo">{t("social.profilo_testo", { nome: p.nome })}</p>
            <CampoNome valore={nome} onCambia={setNome} />
            <button type="submit" className="pulsante pulsante-primario" disabled={invio}>
              {t("social.sfida_nome", { nome: p.nome })}
            </button>
          </form>
        )}
        {/* v033: aggiungere agli amici (con l'account, non il proprio profilo) */}
        {!mio && p.amicizia && <PulsanteAmicizia stato={p.amicizia} chiedi={() => chiediAmiciziaProfilo(p.nomeUtente)} nome={p.nome} />}
        {errore === "sospeso" ? (
          <AvvisoSospensione />
        ) : errore && (
          <p className="admin-errore" role="alert">
            {t(`social.errore_${errore}`, { defaultValue: t("gioco.errore_generico") })}
          </p>
        )}
        <button type="button" className="pulsante pulsante-secondario" onClick={onEsci}>
          {t("gioco.torna_home")}
        </button>
        {/* v039: bloccare senza segnalare (solo con l'account, non il proprio profilo) */}
        {!mio && p.id !== undefined && !p.bloccato && (
          <button type="button" className="pulsante-link ld-blocca" onClick={() => setChiediBlocco(true)}>
            {t("amici.blocca_nome", { nome: p.nome })}
          </button>
        )}
      </main>
      <Finestra aperta={chiediBlocco} titolo={t("amici.blocca_nome", { nome: p.nome })} onChiudi={() => setChiediBlocco(false)}>
        <p>{t("amici.blocca_testo", { nome: p.nome })}</p>
        <div className="azioni-riga">
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setChiediBlocco(false)}>
            {t("generale.annulla")}
          </button>
          <button
            type="button"
            className="pulsante pulsante-pericolo"
            onClick={() => {
              setChiediBlocco(false);
              void bloccaPersona({ nome: p.nomeUtente }).finally(() => setGiro((g) => g + 1));
            }}
          >
            {t("amici.blocca")}
          </button>
        </div>
      </Finestra>
    </div>
  );
}
